const IMPERSONATION_STORAGE_KEY = "hallticket.spot.impersonation.v1";

export interface SpotImpersonationSession {
  token: string;
  tokenType: string;
  expiresAt: number;
  collegeId: number;
  impersonatedUsername?: string;
  actorUsername?: string;
}

function canUseWebStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function parseStoredSession(raw: string): SpotImpersonationSession | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      return null;
    }

    const token = typeof parsed.token === "string" ? parsed.token.trim() : "";
    const tokenType = typeof parsed.tokenType === "string" ? parsed.tokenType.trim() : "";
    const expiresAt = toFiniteNumber(parsed.expiresAt);
    const collegeId = toFiniteNumber(parsed.collegeId);

    if (!token || !tokenType || expiresAt == null || collegeId == null) {
      return null;
    }

    const session: SpotImpersonationSession = {
      token,
      tokenType,
      expiresAt,
      collegeId,
    };

    if (typeof parsed.impersonatedUsername === "string" && parsed.impersonatedUsername.trim()) {
      session.impersonatedUsername = parsed.impersonatedUsername.trim();
    }
    if (typeof parsed.actorUsername === "string" && parsed.actorUsername.trim()) {
      session.actorUsername = parsed.actorUsername.trim();
    }

    return session;
  } catch {
    return null;
  }
}

function readPersistedSession(): SpotImpersonationSession | null {
  if (!canUseWebStorage()) {
    return null;
  }

  // localStorage so a College Admin tab opened via window.open can read the
  // token started from the Super Admin tab (sessionStorage is per-tab only).
  const raw = window.localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  if (!raw) {
    // Migrate any legacy sessionStorage value from older builds.
    try {
      const legacy = window.sessionStorage?.getItem(IMPERSONATION_STORAGE_KEY);
      if (legacy) {
        window.localStorage.setItem(IMPERSONATION_STORAGE_KEY, legacy);
        window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        return parseStoredSession(legacy);
      }
    } catch {
      // ignore
    }
    return null;
  }

  const session = parseStoredSession(raw);
  if (!session) {
    window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  }
  return session;
}

function persistSession(session: SpotImpersonationSession | null): void {
  if (!canUseWebStorage()) {
    return;
  }

  if (!session) {
    window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    try {
      window.sessionStorage?.removeItem(IMPERSONATION_STORAGE_KEY);
    } catch {
      // ignore
    }
    return;
  }

  window.localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
}

/** Unix-seconds expiry check (backend `expiresAt`). */
export function isImpersonationSessionExpired(session: SpotImpersonationSession): boolean {
  const nowSeconds = Math.floor(Date.now() / 1000);
  return session.expiresAt <= nowSeconds;
}

export function getImpersonationSession(): SpotImpersonationSession | null {
  // Always re-read so Exit / expiry in the impersonated tab is visible here too.
  return readPersistedSession();
}

/**
 * Returns a non-expired session, or clears and returns null when expired.
 */
export function getActiveImpersonationSession(): SpotImpersonationSession | null {
  const session = getImpersonationSession();
  if (!session) {
    return null;
  }

  if (isImpersonationSessionExpired(session)) {
    console.info("[impersonation] Token expired — clearing impersonation state", {
      collegeId: session.collegeId,
      expiresAt: session.expiresAt,
    });
    clearImpersonationSession();
    return null;
  }

  return session;
}

export function isImpersonating(): boolean {
  return getActiveImpersonationSession() !== null;
}

export function setImpersonationSession(session: SpotImpersonationSession): void {
  persistSession(session);
}

export function clearImpersonationSession(): void {
  persistSession(null);
}
